import { LLMResponse, RiskReportDisplay } from "../types";

class LLMService {
    private listeners: ((riskAnalysis: LLMResponse) => void)[] = [];

    subscribe = (listener: (data: LLMResponse) => void) => {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    notifySubscribers(riskAnalysis: LLMResponse) {
        this.listeners.forEach((listener) => listener(riskAnalysis));
    }

    getLastRiskAnalysis = async (): Promise<LLMResponse> => {
        const url = "/quoter/last-risk-analysis";
        try {
            const response = await fetch(url, { method: 'GET' });
            if (response.ok) {
                const json = await response.json();
                return json.llmResponse;
            } else {
                throw new Error("Error fetching Last Risk Analysis");
            }
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseRisk = async (question: string, message: string): Promise<[boolean, LLMResponse]> => {
        const url = `/quoter/llm-analyse-risk?question=${question}&input=${message}`;

        try {
            const response = await fetch(url, { method: 'POST' });

            if (response.ok) {
                const json = await response.json();
                this.notifySubscribers(json.llmResponse);
                return [json.valid, json.llmResponse];
            } else {
                throw new Error("Error fetching LLM Analysis");
            }

        } catch (error) {
            console.error(error);
        }
    }

    analyseFlags = async (): Promise<LLMResponse> => {
        const url = "/quoter/llm-analyse-flags";
        try {
            const response = await fetch(url, { method: 'GET' });

            if (response.ok) {
                const json = await response.json();
                return json.llmResponse;
            } else {
                throw new Error("Error fetching LLM Analysis");
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseConversation = async (question: string, answer: string): Promise<ConversationResponse> => {
        const url = `/quoter/llm-analyse-conversation?question=${question}&answer=${answer}`;

        try {
            const response = await fetch(url, { method: 'POST' });

            if (response.ok) {
                const json = await response.json();
                this.notifySubscribers(json.llmResponse);
                return json as ConversationResponse;
            } else {
                throw new Error("Error fetching LLM Conversation Analysis");
            }

        } catch (error) {
            console.error(error);
        }
    }

    getInitialQuestion = async (): Promise<ConversationResponse> => {
        const url = `quoter/llm-analyse-conversation`;
        const response = await fetch(url, { method: 'POST' });

        if (response.ok) {
            const json = await response.json();
            this.notifySubscribers(json.llmResponse);
            return json as ConversationResponse;
        } else {
            throw new Error("Error getting initial Question")
        }
    }

    postFlagQuestionAnswers = async (answers: string): Promise<boolean> => {
        const url = `quoter/llm-flag-questions?answers=${answers}`;
        const response = await fetch(url, { method: 'POST' });
        return response.ok;
    }

    getWebsiteCheck = async (websiteUrl: string): Promise<boolean> => {
        const url = `/quoter/website-check?url=${websiteUrl}`;
        try {
            const response = await fetch(url);
            return response.ok;

        } catch (error) {
            console.error('Error during website-check:', error);
            return false
        }
    }

    getWebsiteCheckResult = async (): Promise<{result: string}> => {
        const url = "/quoter/website-check-result";
        try {
            const response = await fetch(url);
            if (!response.ok) return null;

            const json = await response.json();
            return json;
        } catch (error) {
            console.error('Error during website-check-result:', error);
            return { result: "error"}
        }
    }

    answerConsistencyQuestion = async (answer: string): Promise<ConsistencyQuestionResponse> => {
        const url = `/quoter/llm-consistency-question?answer=${answer}`;
        try {
            const response = await fetch(url, { method: 'POST' });

            if (response.ok) {
                const json = await response.json();
                return json;
            } else {
                throw new Error("Error fetching LLM Consistency Answer");
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    analyseConsistency = async (): Promise<LLMResponse> => {
        const url = `/quoter/llm-analyse-consistency`;
        try {
            const response = await fetch(url, { method: 'GET' });

            if (response.ok) {
                const json = await response.json();
                return json.llmResponse;
            } else {
                throw new Error("Error fetching LLM Consistency");
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    getLastConsistencyAnalysis = async (): Promise<LLMResponse> => {
        const url = `/quoter/last-consistency-analysis`;
        try {
            const response = await fetch(url, { method: 'GET' });

            if (response.ok) {
                const json = await response.json();
                return json.llmResponse;
            } else {
                throw new Error("Error fetching Last LLM Consistency");
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    getRiskReport = async (): Promise<RiskReportDisplay> => {
        const url = `/quoter/risk-report`;
        try {
            const response = await fetch(url, { method: 'GET' });

            if (response.ok) {
                const json = await response.json();
                return json.riskReport;
            } else {
                throw new Error("Error fetching risk report");
            }

        } catch (error) {
            console.error(error);
            return null;
        }
    }
}

export default new LLMService();

interface ConversationResponse {
    llmResponse: LLMResponse;
    valid: boolean;
    finished: boolean;
}

interface ConsistencyQuestionResponse {
    llmResponse: LLMResponse;
    valid: boolean;
}
